/**
 * VskiSetupPanel.tsx — configure the backend's project storage (VSKI platform).
 *
 * A status-driven form reused by Settings → Storage. The backend is the
 * broker: it probes the platform, and the panel walks the states:
 *
 *   configured            → green summary + Clear (stored credentials only)
 *   no platform found     → custom-URL field + Check
 *   platform, no admins   → link to the platform's /installer first-run page
 *   platform, no key      → the credential form (VskiCredentialForm — shared
 *                           with the Connectivity wizard's login step)
 *
 * Key material never crosses this boundary — responses carry only `source`.
 * Inline ok/fail feedback follows the .api-connect-test conventions.
 */
import { useState } from 'react';

import { clearVskiPlatform, useVskiStatus } from '../vski/vskiConfig';
import { VskiCredentialForm } from './VskiCredentialForm';

interface VskiSetupPanelProps {
  /** HTTP origin of the connected backend (the status/mutation base). */
  backendOrigin: string | null;
  /** The backend API key entered at connect time (bearer for the routes). */
  token: string | null;
}

type Feedback = { kind: 'ok'; text: string } | { kind: 'fail'; text: string } | null;

export function VskiSetupPanel({
  backendOrigin,
  token,
}: VskiSetupPanelProps): JSX.Element {
  const [probeUrl, setProbeUrl] = useState<string | null>(null);
  const [customUrl, setCustomUrl] = useState('');
  const [busy, setBusy] = useState<'clear' | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const { status, loading, error, reload } = useVskiStatus(backendOrigin, token, probeUrl);

  const platformUrl = (): string | undefined =>
    (probeUrl || status?.suggestedUrl || status?.url || customUrl || '').trim() || undefined;

  const runClear = (): void => {
    if (!backendOrigin) return;
    setBusy('clear');
    setFeedback(null);
    clearVskiPlatform(backendOrigin, token)
      .then((r) => {
        if (r.ok) {
          setFeedback({ kind: 'ok', text: 'Stored credential removed.' });
          reload();
        } else {
          setFeedback({ kind: 'fail', text: `✗ ${r.detail ?? 'could not clear'}` });
        }
      })
      .catch((e: Error) => setFeedback({ kind: 'fail', text: `✗ ${e.message}` }))
      .finally(() => setBusy(null));
  };

  const checkCustomUrl = (): void => {
    const trimmed = customUrl.trim();
    if (!trimmed) return;
    setProbeUrl(trimmed.replace(/\/+$/, ''));
    setFeedback(null);
  };

  // ─── render ───────────────────────────────────────────────────────────—

  if (!backendOrigin) {
    return (
      <div className="vski-setup">
        <p className="api-connect-hint">Connect to a backend first.</p>
      </div>
    );
  }
  if (loading && !status) {
    return (
      <div className="vski-setup">
        <p className="api-connect-hint">Checking platform storage…</p>
      </div>
    );
  }
  if (error && !status) {
    return (
      <div className="vski-setup">
        <div className="api-connect-test test-fail">✗ {error}</div>
        <div className="api-connect-actions">
          <button type="button" className="btn" onClick={reload}>Retry</button>
        </div>
      </div>
    );
  }
  if (!status) return <div className="vski-setup" />;

  const { platform } = status;

  return (
    <div className="vski-setup">
      <p className="api-connect-hint">
        The backend stores only an encrypted
        admin API key - never your password.
      </p>

      {!status.sdk && (
        <div className="api-connect-test test-fail">
          Backend is missing the vski-sdk (the <code>[vski]</code> extra) —
          storage cannot use the platform.
        </div>
      )}

      {status.configured && (
        <div className="vski-setup-card vski-ok">
          <div className="vski-setup-title">
            ✓ Storage: VSKI Platform
          </div>
          <div className="vski-setup-meta">
            <code>{status.url}</code>
            <span className="vski-setup-source">key: {status.source === 'env' ? 'env var (operator-managed)' : 'stored (encrypted)'}</span>
            {platform.authOk === false && (
              <span className="vski-setup-warn">key rejected by the platform — re-connect below</span>
            )}
          </div>
          <div className="api-connect-actions">
            <button type="button" className="btn" onClick={reload}>Refresh</button>
            {status.source === 'stored' && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={runClear}
                disabled={busy !== null}
              >
                {busy === 'clear' ? 'Clearing…' : 'Clear stored key'}
              </button>
            )}
          </div>
        </div>
      )}

      {status.configured && platform.authOk === false && (
        <div className="vski-setup-card">
          <div className="vski-setup-title">Re-connect</div>
          <VskiCredentialForm
            backendOrigin={backendOrigin}
            token={token}
            platformUrl={platformUrl()}
            keystore={status.keystore}
            onChanged={reload}
          />
        </div>
      )}

      {!status.configured && !platform.reachable && (
        <div className="vski-setup-card">
          <div className="vski-setup-title">No platform found</div>
          <p className="api-connect-hint">
            {platform.detail
              ? `Probe failed: ${platform.detail}`
              : 'The backend found no platform at the conventional addresses (http://vski:3000, http://127.0.0.1:3000). Start one (docker compose up -d vski) or enter its address:'}
          </p>
          <label className="field">
            <span className="field-label">Platform URL</span>
            <input
              type="text"
              className="text-input"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              placeholder="http://localhost:3000"
              spellCheck={false}
            />
          </label>
          <div className="api-connect-actions">
            <button type="button" className="btn" onClick={checkCustomUrl} disabled={!customUrl.trim()}>
              Check
            </button>
            <button type="button" className="btn" onClick={reload}>Retry</button>
          </div>
        </div>
      )}

      {!status.configured && platform.reachable && !platform.hasAdmins && (
        <div className="vski-setup-card">
          <div className="vski-setup-title">Create the platform administrator</div>
          <p className="api-connect-hint">
            The platform has no superuser yet. Open its first-run installer,
            choose an email + password, then come back — the key minting below
            uses those credentials once and never stores the password.
          </p>
          {status.installerUrl && (
            <div className="api-connect-actions">
              <a
                className="btn btn-start"
                href={status.installerUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open the installer ↗
              </a>
              <button type="button" className="btn" onClick={reload}>
                I created the admin — recheck
              </button>
            </div>
          )}
        </div>
      )}

      {!status.configured && platform.reachable && platform.hasAdmins && (
        <div className="vski-setup-card">
          <div className="vski-setup-title">Connect the platform</div>
          <VskiCredentialForm
            backendOrigin={backendOrigin}
            token={token}
            platformUrl={platformUrl()}
            keystore={status.keystore}
            onChanged={reload}
          />
        </div>
      )}

      {feedback && (
        <div className={`api-connect-test test-${feedback.kind}`}>{feedback.text}</div>
      )}
    </div>
  );
}
